import React from "react";

// MSAL imports
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import ReactDOM from "react-dom/client";

import App from "./App";
import { msalConfig } from "./authConfig";
import "./index.css";
import {
  clearLegacyMsalLocalStorage,
  wasExplicitlyLoggedOut,
} from "./utils/cookies";
import {
  dropEndedTabSession,
  ssoSilentOnce,
  trackTabSessionStart,
} from "./utils/msalTabSession";

// MDRS-556: the MSAL cache now lives in sessionStorage; drop the copy an
// earlier build left in localStorage.
clearLegacyMsalLocalStorage(msalConfig.auth.clientId);

export const msalInstance = new PublicClientApplication(msalConfig);
// Before initialize(), so the sign-in a redirect completes is stamped too.
trackTabSessionStart(msalInstance);

msalInstance.initialize().then(async () => {
  // Local only (no network), and must run before MsalProvider reads the cache.
  await dropEndedTabSession(msalInstance);

  // Paint IMMEDIATELY after initialize. The auth bootstrap below (redirect
  // handling + silent SSO) must NOT gate the first render: on a fresh/incognito
  // session ssoSilent opens a hidden iframe to Entra that hangs until MSAL's
  // ~10s iframe timeout, which showed as a ~10-15s white screen. MsalProvider and
  // the event callback keep account state consistent once the background auth
  // resolves, so nothing is lost by rendering first.
  // Only bootstrap when the host page actually has a #root (i.e. the real app
  // shell). Tests import this module for its `msalInstance` export, which pulls
  // the whole entry point in; without this guard `createRoot(null)` throws as an
  // unhandled rejection under jsdom/happy-dom, and the redirect/ssoSilent calls
  // below fire a real network request that races happy-dom's teardown and
  // surfaces as a flaky "AsyncTaskManager has been destroyed" error in whatever
  // test happens to be running when it aborts. In prod #root always exists, so
  // behaviour is unchanged.
  const container = document.getElementById("root");
  if (!container) {
    return;
  }

  const root = ReactDOM.createRoot(container);
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </React.StrictMode>
  );

  // Register listeners before the async auth so no LOGIN/SSO events are missed.
  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS
    ) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
    }
  });
  // The cache is tab-scoped now (MDRS-556), so this only hears tabs still on a
  // build that used localStorage. Cross-tab logout uses onExplicitLogoutElsewhere.
  msalInstance.enableAccountStorageEvents();

  // Auth bootstrap runs in the BACKGROUND — never blocks the first paint.
  (async () => {
    try {
      const redirectResponse = await msalInstance.handleRedirectPromise();
      if (redirectResponse && redirectResponse.account) {
        msalInstance.setActiveAccount(redirectResponse.account);
      }
    } catch (_error) {
      // Intentionally suppress auth debug logging in production builds.
    }

    // Default to the first account if none active on page load.
    if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    } else if (!msalInstance.getActiveAccount() && !wasExplicitlyLoggedOut()) {
      // Silent SSO for new tabs in the same browser. Backgrounded so its
      // hidden-iframe timeout never delays first paint (this was the culprit).
      // Skipped after an explicit Log Out (MDRS-383 follow-up): Microsoft's
      // own SSO session can outlive our app-side logout, so without this
      // guard, a refresh/new-tab/direct-link would silently sign the user
      // back in even though they just explicitly logged out.
      try {
        await ssoSilentOnce(msalInstance);
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
      } catch (_error) {
        // Intentionally suppress auth debug logging in production builds.
      }
    }
  })();
});
